import { Component, Input, OnInit } from '@angular/core';
import { ChartBaseDirective } from '@client/app/modules/charts/directives/chart-base.directive';
import { DEFAULT_CHART_SETTINGS } from '@client/app/modules/charts/constants/Chart.constant';
import { LegendCursorService } from '@client/app/modules/charts/services/LegendCursor.service';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'mon-chart-barline',
    templateUrl: './barline.component.html',
    standalone: true,
    imports: [
        ChartBaseDirective,
        CommonModule,
    ],
})
export class BarlineComponent implements OnInit {
    @Input() labels: any[] = [];
    @Input() data: any[] = [];
    @Input() series: any[] = [];
    @Input() override: any[] = [];
    @Input() options: any = {};
    @Input() height: any = '';
    @Input() colors: any[] = [
        DEFAULT_CHART_SETTINGS.CHART.COLORS.LIGHT_BLUE,
        DEFAULT_CHART_SETTINGS.CHART.COLORS.BLUE,
        DEFAULT_CHART_SETTINGS.CHART.COLORS.LIGHT_GRAY,
        DEFAULT_CHART_SETTINGS.CHART.COLORS.GREEN,
        DEFAULT_CHART_SETTINGS.CHART.COLORS.ORANGE,
        DEFAULT_CHART_SETTINGS.CHART.COLORS.GRAY,
        DEFAULT_CHART_SETTINGS.CHART.COLORS.DARK_GRAY,
    ];
    @Input() emptyDataMessage: string = '';

    constructor (
        private legendCursorService: LegendCursorService,
        private translateService: TranslateService,
    ) {}

    ngOnInit (): void {
        this.override = this.override || [];

        this.options = this.options || {
            scales: {
                xAxes: [{
                    gridLines: {
                        display: false,
                    },
                }],
            },
            legend: {
                display: true,
                position: 'bottom',
                labels: {
                    boxWidth: 20,
                    padding: 5,
                },
            },
            tooltips: {
                enabled: true,
                mode: 'x-axis',
                bodySpacing: 5,
            },
            hover: { mode: 'x-axis' },
        };

        this.options = this.legendCursorService.setHoverState(this.options);
        this.emptyDataMessage = this.emptyDataMessage || this.translateService.getString('No data present');

        this.activate();
    }

    private activate (): void {
        const pushToOverride = this.override.length === 0;

        if (this.options.scales && this.options.scales.yAxes && pushToOverride) {
            this.options.scales.yAxes.forEach((data: any, index: number) => {
                this.override.push(this.chartDataSet(data.id, this.colors[(index % this.colors.length)]));
            });
        } else if (!(this.options.scales && this.options.scales.yAxes && this.options.scales.yAxes.length > 0)) {
            if (!this.options.scales) {
                this.options.scales = {};
            }
            if (!this.options.scales.yAxes) {
                this.options.scales.yAxes = [];
            }

            this.series.forEach((data: any, index: number) => {
                this.options.scales.yAxes.push({
                    id: data,
                    type: 'linear',
                    display: (index === 0),
                    position: 'left',
                    ticks: {
                        beginAtZero: true,
                        suggestedMax: 4,
                        maxTicksLimit: 5,
                    },
                });

                if (pushToOverride) {
                    this.override.push(this.chartDataSet(data, this.colors[(index % this.colors.length)]));
                }
            });
        }
    }

    private chartDataSet (ID: string, color: string): any {
        return {
            yAxisID: ID,
            fill: false,
            borderWidth: 2.5,
            pointHoverRadius: 6,
            pointRadius: 4,
            pointBackgroundColor: '#fff',
            pointHoverBorderColor: '#fff',
            backgroundColor: `rgba(${color}, 0.2)`,
            borderColor: `rgba(${color}, 1)`,
            pointBorderColor: `rgba(${color}, 1)`,
            pointHoverBackgroundColor: `rgba(${color}, 1)`,
            pointHitRadius: 15,
            lineTension: 0.1,
        };
    }

    areAllDataRowsEmpty (): boolean {
        if (!Array.isArray(this.data)) {
            return true;
        }

        for (let i = 0; i < this.data.length; i++) {
            if (Array.isArray(this.data[i])) {
                if (this.data[i].length > 0) {
                    return false;
                }
            }
        }
        return true;
    }
}
